/**
 * Created by Alex Xia on 6/10/2020.
 * Description:
 * Add entity panel component to show slide sheet
 * ------ maintenance history ------
 */
import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { EntityConfig, EntityAction, EntityActionTypes } from '../../pages/entity/entity.model';
import { EntityDetailConfig } from '../entity-detail/entity-detail.config';
import { RelationshipTreeConfig, RelationshipTreeActions } from '../relationship-tree/relationship-tree.view-model';
import { TabOptions } from '../../widgets/tabs/tabs.model';
import { Entity } from '../../tamalelibs/models/entity.model';
import { Router } from '@angular/router';
import { EntityViewModel } from '../../tamalelibs/components/entity.view-model';
import { EntityService } from '../../tamalelibs/services/entity.service';
import { businessConstants } from '../../constants/business.constants';
import { AppState } from '../../redux';
import { RelationshipTreeService } from '../../services/relationship-tree.service';
import { getEntityMsgState, getSlideSheetEntityState, getTemplateSlideSheetConfigState, getSlideSheetEntityMsgState } from '../../redux/reducers/entity-detail-view.reducer';
import { skip } from 'rxjs/operators';

@Component({
    selector: 'entity-panel',
    templateUrl: './entity-panel.component.html',
    styleUrls: ['./entity-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityPanelComponent implements OnInit, OnDestroy {
    @Input() entityID: string;
    @Input() eventKey?: string;
    @Input() eventValue?: string;
    @Input() config: EntityConfig;

    currentEntity: Entity;
    entityConfig: EntityDetailConfig = new EntityDetailConfig();
    relationshipTreeConfig: RelationshipTreeConfig = new RelationshipTreeConfig();
    isPageLoading: boolean;

    // for tabs
    selectedIndex = 0;
    tabsOptions: TabOptions;
    menuItems: Array<any> = [{
        title: 'Detail',
        disabled: false,
        editable: false,
    }];
    draggableTab = false;

    private _subscribed = false;
    private _destroySubscriptions: Array<Subscription> = [];
    private _templateConfigArray = [];

    constructor(
        private _router: Router,
        private _entityViewModel: EntityViewModel,
        private _entityService: EntityService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _relationshipTreeService: RelationshipTreeService,
        private _store: Store<AppState>,
    ) { }

    ngOnInit() {
        this._templateConfigArray = [];
        this.isPageLoading = true;
        this.entityConfig.embedMode = true;

        this._initSubscriptions();
        this._initTwoTabOptions();

        if (this.config && this.config.embedMode) {
            this.config.actionSubject$.subscribe((action: EntityAction) => {
                if (action.type === EntityActionTypes.SWITCH_ANOTHER_ENTITY) {
                    this.selectedIndex = 0;
                    this.isPageLoading = true;
                    this._init(action.payload.id);
                }
            });
        } else {
            this._init(this.entityID);
        }

        this._updateChangesToUI();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._subscribed = false;
    }

    /**
     * tab change
     * @param currentTabIndex
     */
    selectChange(currentTabIndex: number) {
        this.selectedIndex = currentTabIndex;
        this._updateChangesToUI();
    }

    trackByFn(index, item) {
        return item.id;
    }

    private _initSubscriptions() {
        if (!this._subscribed) {
            this._subscribed = true;

            this._destroySubscriptions.push(
                this._store
                    .pipe<Entity>(select(getSlideSheetEntityState))
                    .pipe<Entity>(skip(1))
                    .subscribe(res => this._onSubscribeEntity(res))
            );

            this._destroySubscriptions.push(
                this._store.pipe<string>(
                    select(getEntityMsgState),
                ).subscribe(msg => this._onSubscribeEntityMsg(msg))
            );

            this._destroySubscriptions.push(
                this._store
                    .pipe<any>(select(getTemplateSlideSheetConfigState))
                    .pipe<any>(skip(1))
                    .subscribe(res => this._onSubscribeTemplateConfig(res))
            );

            this._relationshipTreeService.relationshipTreeOperate$.subscribe(action => {
                if (action.type === RelationshipTreeActions.REMOVE_RELATIONSHIP
                    || action.type === RelationshipTreeActions.ADD_RELATIONSHIP
                    || action.type === RelationshipTreeActions.CHANGE_RELATIONSHIP) {
                    this._refreshEntityPanel();
                }
            });
        }
    }
    private _init(entityId: string) {
        if (entityId) {
            this._entityViewModel.getSlideSheetEntity(entityId);
        }
    }

    private _initTwoTabOptions() {
        this.menuItems = [{
            title: 'Detail',
            disabled: false,
            editable: false,
        }, {
            title: 'Relationship',
            disabled: false
        }];
    }

    /**
     * get adhoc value of the entity
     * @param _config
     */
    private _onSubscribeTemplateConfig(_config: any): void {
        if (this._templateConfigArray.findIndex(item => item.entity.id === this.currentEntity.id) < 0) {
            this._templateConfigArray.push({
                entity: this.currentEntity,
                config: _config
            });
        }
        this.entityConfig.customMetadata = [];
        this._templateConfigArray.map(item => {
            if (item.entity && item.entity.id === this.currentEntity.id) {
                if (item.config) {
                    this.entityConfig.customMetadata = this._entityService.getCustomMetadata(this.currentEntity, item.config);
                }
            }
        });
        this.isPageLoading = false;
        this._updateChangesToUI();
    }

    private _onSubscribeEntity(_entity: Entity): void {
        if (_entity) {
            this.entityConfig.entity = this.currentEntity = _entity;

            this._showEntityAdhocs(_entity);
            this._showRelationshipTree(_entity);
        }
    }

    private _showEntityAdhocs(_entity: Entity) {
        this._entityViewModel.getSlideSheetTemplateConfig(_entity.type.id);
    }

    private _showRelationshipTree(_entity: Entity) {
        this.relationshipTreeConfig.checkable = false;
        this.relationshipTreeConfig.expandable = true;

        const treeNodes = RelationshipTreeService.parseToTreeViewModel(_entity);
        this.relationshipTreeConfig.nodes = treeNodes;
        this.relationshipTreeConfig.showAs = 'shortName';
        this.relationshipTreeConfig.expandedKeys = [];
        treeNodes.forEach(element => {
            if (element.uuid !== '1' && element.level !== '4') {
                this.relationshipTreeConfig.expandedKeys.push(element.uuid);
            }
        });

        this._updateChangesToUI();
    }

    private _onSubscribeEntityMsg(msg: string) {
        if (msg.indexOf('GET_ENTITY_FAILURE') > -1) {
            this._router.navigate(['/error']);
        }
    }

    private _refreshEntityPanel() {
        if (this.currentEntity.id) {
            this._init(this.currentEntity.id);
        }
    }


    private _updateChangesToUI() {
        if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
            this._changeDetectorRef.detectChanges();
        }
    }
}
